// @ts-nocheck
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Download, Eye, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import ProcessPayoutDialog from './ProcessPayoutDialog';

interface VendorPayout {
  id: string;
  vendor_name: string;
  email: string;
  total_orders: number;
  total_revenue: number;
  payout_amount: number;
  commission_rate: number;
  last_payout_date: string | null;
  w9_status: 'submitted' | 'pending' | 'expired';
  performance_rating: number;
  is_active: boolean;
}

const VendorPayoutsTab: React.FC = () => {
  const [selectedVendors, setSelectedVendors] = useState<string[]>([]);
  const [expandedVendor, setExpandedVendor] = useState<string | null>(null);
  const [bulkAction, setBulkAction] = useState('');
  const [selectedVendor, setSelectedVendor] = useState<any>(null);
  const [showPayoutDialog, setShowPayoutDialog] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const queryClient = useQueryClient();

  const { data: vendors, isLoading, error } = useQuery({
    queryKey: ['vendor-payouts'],
    queryFn: async () => {
      console.log('🔍 Fetching vendor payouts...');
      
      // Fetch all vendors (including inactive ones with orders)
      const { data: vendorData, error: vendorError } = await supabase
        .from('graphics_vendor_profiles')
        .select('id, business_name, contact_email, rating_average, review_count, user_id, is_active, commission_rate');

      if (vendorError) {
        console.error('❌ Vendor fetch error:', vendorError);
        throw new Error(`Failed to fetch vendors: ${vendorError.message}`);
      }

      console.log('✅ Vendors fetched:', vendorData?.length || 0);

      // Fetch venmo handles from profiles for all vendor user_ids
      const vendorUserIds = (vendorData || []).map(v => v.user_id).filter(Boolean);
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, venmo_handle')
        .in('id', vendorUserIds);
      
      const venmoByUserId: Record<string, string> = {};
      profilesData?.forEach(p => { if (p.venmo_handle) venmoByUserId[p.id] = p.venmo_handle; });

      // Fetch all completed orders
      const { data: ordersData, error: ordersError } = await supabase
        .from('graphics_orders')
        .select('vendor_id, total_amount, status, updated_at')
        .eq('status', 'completed');

      if (ordersError) {
        console.error('❌ Orders fetch error:', ordersError);
        throw new Error(`Failed to fetch orders: ${ordersError.message}`);
      }

      console.log('✅ Orders fetched:', ordersData?.length || 0);

      // Fetch W-9 forms for all vendors
      const { data: w9Data, error: w9Error } = await supabase
        .from('tax_forms')
        .select('user_id, status, submitted_at')
        .eq('form_type', 'w9')
        .eq('status', 'submitted');

      if (w9Error) {
        console.error('❌ W-9 fetch error:', w9Error);
      }

      // Create lookup for W-9 status by user_id
      const w9StatusByUserId = (w9Data || []).reduce((acc, form) => {
        acc[form.user_id] = 'submitted';
        return acc;
      }, {} as Record<string, 'submitted'>);

      // Group orders by vendor_id
      const ordersByVendor = (ordersData || []).reduce((acc, order) => {
        if (!acc[order.vendor_id]) {
          acc[order.vendor_id] = [];
        }
        acc[order.vendor_id].push(order);
        return acc;
      }, {} as Record<string, any[]>);

      // Combine data
      const vendorsWithStats = (vendorData || []).map((vendor) => {
        const orders = ordersByVendor[vendor.id] || [];
        const totalRevenue = orders.reduce((sum, order) => sum + (order.total_amount || 0), 0);
        const commissionRate = Number(vendor.commission_rate) || 15; // Default to 15% if not set
        const vendorPayout = totalRevenue * (commissionRate / 100);
        const w9Status = w9StatusByUserId[vendor.user_id] || 'pending';

        return {
          id: vendor.id,
          vendor_name: vendor.business_name || 'Unknown Vendor',
          email: vendor.contact_email || 'No email',
          total_orders: orders.length,
          total_revenue: totalRevenue,
          payout_amount: vendorPayout,
          commission_rate: commissionRate,
          last_payout_date: orders[0]?.updated_at || null,
          w9_status: w9Status as 'submitted' | 'pending' | 'expired',
          performance_rating: Number(vendor.rating_average) || 0,
          is_active: vendor.is_active,
          venmo_handle: venmoByUserId[vendor.user_id] || null,
        };
      }).sort((a, b) => b.payout_amount - a.payout_amount); // Sort high to low

      console.log('✅ Processed all vendors:', vendorsWithStats.length);
      
      return vendorsWithStats;
    },
    retry: 1,
  });

  // Payout history - disabled until graphics_vendor_payouts table is created
  const { data: payoutHistory } = useQuery({
    queryKey: ['graphics-payout-history'],
    queryFn: async () => {
      // Return empty array for now since the table doesn't exist yet
      return [];
    },
    enabled: false // Disable this query
  });

  // Process payout mutation - disabled until graphics_vendor_payouts table is created
  const processPayoutMutation = useMutation({
    mutationFn: async ({ vendorId, payoutData }: { vendorId: string; payoutData: any }) => {
      // TODO: Implement payout processing once graphics_vendor_payouts table exists
      toast.info('Payout processing will be available once the payout tracking table is set up');
      throw new Error('Payout table not yet configured');
    },
    onSuccess: () => {
      toast.success('Payout processed successfully');
      queryClient.invalidateQueries({ queryKey: ['vendor-payouts'] });
      queryClient.invalidateQueries({ queryKey: ['graphics-payout-history'] });
    },
    onError: (error: any) => {
      toast.error(`Failed to process payout: ${error.message}`);
    }
  });

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedVendors(vendors?.map(v => v.id) || []);
    } else {
      setSelectedVendors([]);
    }
  };

  const handleSelectVendor = (vendorId: string, checked: boolean) => {
    if (checked) {
      setSelectedVendors([...selectedVendors, vendorId]);
    } else {
      setSelectedVendors(selectedVendors.filter(id => id !== vendorId));
    }
  };

  const handleBulkAction = () => {
    if (!bulkAction || selectedVendors.length === 0) {
      toast.error('Please select vendors and an action');
      return;
    }

    switch (bulkAction) {
      case 'send-reminders':
        toast.success(`Sending reminders to ${selectedVendors.length} vendors`);
        break;
      case 'request-tax-forms':
        toast.success(`Requesting tax forms from ${selectedVendors.length} vendors`);
        break;
      case 'mark-paid':
        toast.success(`Marking ${selectedVendors.length} vendors as paid`);
        break;
      case 'schedule-payment':
        toast.success(`Scheduling payments for ${selectedVendors.length} vendors`);
        break;
    }
  };

  const exportToCSV = () => {
    if (!vendors) return;

    const headers = ['Vendor Name', 'Email', 'Total Orders', 'Total Revenue', 'Commission Rate', 'Payout Amount', 'W-9 Status', 'Rating'];
    const rows = vendors.map(v => [
      v.vendor_name,
      v.email,
      v.total_orders,
      `$${v.total_revenue.toFixed(2)}`,
      `${v.commission_rate}%`,
      `$${v.payout_amount.toFixed(2)}`,
      v.w9_status,
      v.performance_rating.toFixed(1),
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vendor-payouts-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    
    toast.success('Exported to CSV');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center space-y-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground">Loading vendor payouts...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive mb-4">
              <AlertCircle className="h-5 w-5" />
              <p className="font-semibold">Error loading vendor payouts</p>
            </div>
            <p className="text-sm text-muted-foreground">
              {error instanceof Error ? error.message : 'An unknown error occurred'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleProcessPayout = (vendorId: string, vendor: any) => {
    setSelectedVendor({ ...vendor, vendorId });
    setShowPayoutDialog(true);
  };

  const totalPending = vendors?.reduce((sum, v) => sum + v.payout_amount, 0) || 0;
  const totalPaid = payoutHistory?.reduce((sum, p) => sum + Number(p.adjusted_amount), 0) || 0;

  return (
    <div className="space-y-6">
      {/* Header with toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Graphics Vendor Payouts</h2>
          <p className="text-muted-foreground">Process Venmo payments and track payout history</p>
        </div>
        <Button
          variant={showHistory ? 'default' : 'outline'}
          onClick={() => setShowHistory(!showHistory)}
        >
          {showHistory ? 'Show Pending' : 'View History'}
        </Button>
      </div>

      {!showHistory ? (
        <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Pending Payouts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalPending)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Vendors with Payouts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{vendors?.length || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Missing W-9 Forms</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {vendors?.filter(v => v.w9_status === 'pending').length || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Avg Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {vendors && vendors.length > 0
                ? (vendors.reduce((sum, v) => sum + v.performance_rating, 0) / vendors.length).toFixed(1)
                : '0.0'} ⭐
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions Bar */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle>Vendor Payouts</CardTitle>
              <CardDescription>Manage graphics vendor payments and tax forms</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={bulkAction} onValueChange={setBulkAction}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Bulk Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="send-reminders">Send Reminders</SelectItem>
                  <SelectItem value="request-tax-forms">Request Tax Forms</SelectItem>
                  <SelectItem value="mark-paid">Mark as Paid</SelectItem>
                  <SelectItem value="schedule-payment">Schedule Payment</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleBulkAction} disabled={selectedVendors.length === 0}>
                Apply
              </Button>
              <Button variant="outline" onClick={exportToCSV}>
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!vendors || vendors.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No vendors with pending payouts found.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedVendors.length === vendors.length}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="text-right">Orders</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                  <TableHead className="text-right">Commission</TableHead>
                  <TableHead className="text-right">Payout</TableHead>
                  <TableHead>W-9 Status</TableHead>
                  <TableHead className="text-right">Rating</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vendors.map((vendor) => (
                  <React.Fragment key={vendor.id}>
                    <TableRow>
                      <TableCell>
                        <Checkbox
                          checked={selectedVendors.includes(vendor.id)}
                          onCheckedChange={(checked) => handleSelectVendor(vendor.id, checked as boolean)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        {vendor.vendor_name}
                        {!vendor.is_active && (
                          <Badge variant="outline" className="ml-2 text-xs">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{vendor.email}</TableCell>
                      <TableCell className="text-right">{vendor.total_orders}</TableCell>
                      <TableCell className="text-right">{formatCurrency(vendor.total_revenue)}</TableCell>
                      <TableCell className="text-right">{vendor.commission_rate}%</TableCell>
                      <TableCell className="text-right font-semibold">{formatCurrency(vendor.payout_amount)}</TableCell>
                      <TableCell>
                        <Badge variant={vendor.w9_status !== 'pending' ? 'default' : 'destructive'}>
                          {vendor.w9_status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">{vendor.performance_rating.toFixed(1)} ⭐</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          onClick={() => handleProcessPayout(vendor.id, vendor)}
                          disabled={vendor.w9_status !== 'submitted'}
                        >
                          Pay
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setExpandedVendor(expandedVendor === vendor.id ? null : vendor.id)}
                        >
                          {expandedVendor === vendor.id ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                    
                    {expandedVendor === vendor.id && (
                      <TableRow>
                        <TableCell colSpan={10} className="bg-muted/50 p-6">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            <div>
                              <h4 className="text-sm font-semibold text-muted-foreground mb-2">Business Details</h4>
                              <div className="space-y-1 text-sm">
                                <p><strong>Business Name:</strong> {vendor.vendor_name}</p>
                                <p><strong>Email:</strong> {vendor.email}</p>
                                <p><strong>Status:</strong> {vendor.is_active ? 'Active' : 'Inactive'}</p>
                              </div>
                            </div>
                            
                            <div>
                              <h4 className="text-sm font-semibold text-muted-foreground mb-2">Payment Info</h4>
                              <div className="space-y-1 text-sm">
                                <p><strong>Commission Rate:</strong> {vendor.commission_rate}%</p>
                                <p><strong>Total Revenue:</strong> {formatCurrency(vendor.total_revenue)}</p>
                                <p><strong>Payout Amount:</strong> {formatCurrency(vendor.payout_amount)}</p>
                              </div>
                            </div>
                            
                            <div>
                              <h4 className="text-sm font-semibold text-muted-foreground mb-2">Performance</h4>
                              <div className="space-y-1 text-sm">
                                <p><strong>Total Orders:</strong> {vendor.total_orders}</p>
                                <p><strong>Rating:</strong> {vendor.performance_rating.toFixed(1)} ⭐</p>
                                <p><strong>Last Payout:</strong> {vendor.last_payout_date ? new Date(vendor.last_payout_date).toLocaleDateString() : 'N/A'}</p>
                              </div>
                            </div>
                            
                            <div>
                              <h4 className="text-sm font-semibold text-muted-foreground mb-2">Tax & Compliance</h4>
                              <div className="space-y-1 text-sm">
                                <p><strong>W-9 Status:</strong> <Badge variant={vendor.w9_status !== 'pending' ? 'default' : 'destructive'}>{vendor.w9_status}</Badge></p>
                              </div>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
      ) : (
        /* Payout History */
        <Card>
          <CardHeader>
            <CardTitle>Payout History</CardTitle>
            <CardDescription>
              Past payments for tax records (${totalPaid.toFixed(2)} total paid this year)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!payoutHistory?.length ? (
              <div className="text-center py-8 text-muted-foreground">
                No payout history yet
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date Paid</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Original</TableHead>
                    <TableHead>Paid</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payoutHistory.map((payout) => (
                    <TableRow key={payout.id}>
                      <TableCell className="font-medium">
                        {new Date(payout.paid_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {payout.graphics_vendor_profiles.business_name}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {payout.payout_method}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {payout.payout_reference || '-'}
                      </TableCell>
                      <TableCell className="font-mono">
                        ${Number(payout.original_amount).toFixed(2)}
                      </TableCell>
                      <TableCell className="font-mono font-bold">
                        ${Number(payout.adjusted_amount).toFixed(2)}
                      </TableCell>
                      <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                        {payout.adjustment_reason || payout.notes || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Payout Dialog */}
      {selectedVendor && (
        <ProcessPayoutDialog
          open={showPayoutDialog}
          onOpenChange={setShowPayoutDialog}
          vendorName={selectedVendor.vendor_name}
          originalAmount={selectedVendor.payout_amount}
          vendorVenmoHandle={selectedVendor.venmo_handle}
          onProcess={async (payoutData) => {
            await processPayoutMutation.mutateAsync({
              vendorId: selectedVendor.vendorId,
              payoutData
            });
          }}
        />
      )}
    </div>
  );
};

export default VendorPayoutsTab;
